import React, { forwardRef, Fragment } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller/src/ui/bottom-right-corner/outlet/index.tsx';
import { fireScreenAnalyticsDeferred, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal/index.tsx';
import { TransitionState } from '../../../controllers/use-minimizable-modal/types.tsx';
import type { MiniContainerProps } from '../../../types.tsx';
import ScrollContainer from './scroll-container/index.tsx';

export const MiniBody = ({ children }: MiniContainerProps): React.JSX.Element => (
	<ScrollContainer>
		<MiniBodyWrapper>{children}</MiniBodyWrapper>
	</ScrollContainer>
);

export const Mini: React.ForwardRefExoticComponent<
	MiniContainerProps & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, MiniContainerProps>(
	({ children, className, components }: MiniContainerProps, ref) => {
		const { Header = Fragment, Footer = Fragment } = components || {};

		const { isTransitionInState } = useMinimizableModal();

		const isViewEntered = isTransitionInState(['mini'], TransitionState.ENTERED);

		return (
			<JiraBottomRightCornerOutlet orderFromRight={3} id="issue-create-mini-modal">
				<ShortcutScope>
					<MiniWrapper
						ref={ref}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className}
						data-testid={!isViewEntered ? undefined : 'minimizable-modal.ui.modal-container.mini'}
						role="dialog"
						aria-modal="false"
						aria-labelledby="minimizable-modal.modal-container.modal-header.title"
					>
						<MountEvent onMount={fireScreenAnalyticsDeferred} />
						{components ? (
							<>
								<Header />
								<MiniBody>{children}</MiniBody>
								<Footer />
							</>
						) : (
							<>{children}</>
						)}
					</MiniWrapper>
				</ShortcutScope>
			</JiraBottomRightCornerOutlet>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniWrapper = styled.div({
	width: '436px',
	height: '569px',
	backgroundColor: token('elevation.surface.overlay'),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.large'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniBodyWrapper = styled.div({
	paddingTop: token('space.100'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});
